import { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Container,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { login, updateUser } from '../../api/tabsApi';
import { TState, useActions } from '../../redux/store';
import { allIcons } from '../utils/Icon';
import Logo from '../layout/Logo';

export const loginFields = [
  { label: 'Identifiant', name: 'login', type: 'text' },
  { label: 'Mot de passe', name: 'pass', type: 'password' },
];

export const updateLoginFields = [
  { label: 'Nouveau mot de passe', name: 'pass', type: 'password' },
];

export const replaceLoginFields = [
  { label: 'Mot de passe actuel', name: 'prevPass', type: 'password' },
  { label: 'Nouveau mot de passe', name: 'pass', type: 'password' },
];

export type TUser = {
  login: string;
  pass: string;
  userColor?: string;
  name?: string;
  _id?: string;
  showActivityLogs?: boolean;
};

type TLogin = {
  handleLogin: (resp: any) => void;
};

const Login = ({ handleLogin }: TLogin) => {
  const toast = useToast();
  const user = useSelector((state: TState) => state.user);
  const theme = useSelector((state: TState) => state.theme);
  const { setUser, setLogin } = useActions();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [prevPass, setPrevPass] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [company, setCompany] = useState(
    (localStorage.getItem('company') !== 'forceLogin'
      ? localStorage.getItem('company')
      : '') || '',
  );
  const [isPWALogin, setIsPWALogin] = useState(
    localStorage.getItem('company') === 'forceLogin',
  );

  const loginType = user.tempPassword
    ? 'updateLogin'
    : user.tempPasswordForced
    ? 'replaceLogin'
    : 'login';

  const isLogin = loginType === 'login';
  const isReplaceLogin = loginType === 'replaceLogin';
  const isUpdateLogin = loginType === 'updateLogin';

  const formData = {
    login: email,
    pass: password,
    prevPass: prevPass,
  };

  const handleConnect = async (formData: TUser) => {
    const resp = await login({
      ...formData,
      company: localStorage.getItem('company'),
    });

    handleLogin(resp);
  };

  const handleUpdate = async (formData: any) => {
    const resp = await updateUser(user._id, {
      ...user,
      ...formData,
      isLogin: true,
    });
    resp && setLogin('');
  };

  const handleSubmit = () => {
    if (
      (isReplaceLogin && (!formData.pass || !formData.prevPass)) ||
      (isLogin && (!formData.login || !formData.pass)) ||
      (isUpdateLogin && !formData.pass)
    ) {
      toast({
        title: 'Champ manquant',
        status: 'error',
        isClosable: true,
      });

      return;
    }

    if (isReplaceLogin || isUpdateLogin) {
      handleUpdate(formData);
    } else {
      handleConnect(formData);
    }
  };

  const handleKeyPress = (e: any) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  // const isExternal = !/evisioncrm|localhost/.test(window.location.hostname);

  const handleSelectCompany = () => {
    if (company) {
      localStorage.setItem('company', company);
      // window.location.href = `/${company}`;
      window.history.replaceState({}, '', `/${company}`);
      setIsPWALogin(false);
    }
  };

  return (
    <Box minH='100vh' bg='gray.100'>
      <Container h='100vh'>
        <Flex h='100%' align='center' justify='center'>
          <Box
            w='full'
            maxW='450px'
            bg='white'
            borderRadius='lg'
            boxShadow='lg'
            p={8}
          >
            <VStack spacing={4} align='stretch'>
              {/* Logo et titre */}
              <VStack spacing={2}>
                <HStack spacing={2} mb={2}>
                  <Logo big />
                </HStack>
                <Text color='gray.600' fontSize='sm'>
                  {isPWALogin ? (
                    'Choisir la société'
                  ) : (
                    <Text textAlign='center'>
                      {`Connectez-vous à votre compte sur la société`}
                      <Text fontWeight='bold' fontSize='xl'>
                        {company}
                      </Text>
                    </Text>
                  )}
                </Text>
                {!isPWALogin && (
                  <Button size='xs' onClick={() => setIsPWALogin(true)}>
                    Changer de société
                  </Button>
                )}
              </VStack>

              {isPWALogin ? (
                <VStack spacing={4}>
                  <FormControl isRequired>
                    <FormLabel color='gray.700' fontSize='sm' fontWeight='500'>
                      Société
                    </FormLabel>
                    <Input
                      type='text'
                      placeholder='Société A'
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      size='lg'
                      borderColor='gray.300'
                      _hover={{ borderColor: 'gray.400' }}
                      _focus={{
                        borderColor: 'blue.500',
                        boxShadow: '0 0 0 1px #3182ce',
                      }}
                    />
                  </FormControl>

                  <HStack>
                    <Button
                      onClick={handleSelectCompany}
                      bgColor={theme.dark}
                      color='white'
                      size='lg'
                      w='full'
                      mt={2}
                      _hover={{ bg: 'blue.600' }}
                    >
                      Valider
                    </Button>
                  </HStack>
                </VStack>
              ) : (
                <VStack spacing={4}>
                  {isLogin && (
                    <FormControl isRequired>
                      <FormLabel
                        color='gray.700'
                        fontSize='sm'
                        fontWeight='500'
                      >
                        Identifiant
                      </FormLabel>
                      <Input
                        type='email'
                        placeholder='exemple@email.com'
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onKeyDown={handleKeyPress}
                        size='lg'
                        borderColor='gray.300'
                        _hover={{ borderColor: 'gray.400' }}
                        _focus={{
                          borderColor: 'blue.500',
                          boxShadow: '0 0 0 1px #3182ce',
                        }}
                      />
                    </FormControl>
                  )}

                  <FormControl isRequired>
                    <FormLabel color='gray.700' fontSize='sm' fontWeight='500'>
                      {isReplaceLogin
                        ? 'Ancien mot de passe'
                        : isUpdateLogin
                        ? 'Nouveau mot de passe'
                        : 'Mot de passe'}
                    </FormLabel>
                    <InputGroup size='lg'>
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        placeholder='••••••••'
                        value={isReplaceLogin ? prevPass : password}
                        onChange={(e) =>
                          isReplaceLogin
                            ? setPrevPass(e.target.value)
                            : setPassword(e.target.value)
                        }
                        onKeyDown={handleKeyPress}
                        borderColor='gray.300'
                        _hover={{ borderColor: 'gray.400' }}
                        _focus={{
                          borderColor: 'blue.500',
                          boxShadow: '0 0 0 1px #3182ce',
                        }}
                      />
                      <InputRightElement>
                        <IconButton
                          aria-label={
                            showPassword
                              ? 'Masquer le mot de passe'
                              : 'Afficher le mot de passe'
                          }
                          icon={
                            showPassword ? (
                              <allIcons.HiEyeSlash />
                            ) : (
                              <allIcons.HiEye />
                            )
                          }
                          onClick={() => setShowPassword(!showPassword)}
                          variant='ghost'
                          size='sm'
                          color='gray.500'
                        />
                      </InputRightElement>
                    </InputGroup>
                  </FormControl>
                  {isReplaceLogin && (
                    <FormControl isRequired>
                      <FormLabel
                        color='gray.700'
                        fontSize='sm'
                        fontWeight='500'
                      >
                        Nouveau mot de passe
                      </FormLabel>
                      <InputGroup size='lg'>
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          placeholder='••••••••'
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          onKeyDown={handleKeyPress}
                          borderColor='gray.300'
                          _hover={{ borderColor: 'gray.400' }}
                          _focus={{
                            borderColor: 'blue.500',
                            boxShadow: '0 0 0 1px #3182ce',
                          }}
                        />
                        <InputRightElement>
                          <IconButton
                            aria-label={
                              showPassword
                                ? 'Masquer le mot de passe'
                                : 'Afficher le mot de passe'
                            }
                            icon={
                              showPassword ? (
                                <allIcons.HiEyeSlash />
                              ) : (
                                <allIcons.HiEye />
                              )
                            }
                            onClick={() => setShowPassword(!showPassword)}
                            variant='ghost'
                            size='sm'
                            color='gray.500'
                          />
                        </InputRightElement>
                      </InputGroup>
                    </FormControl>
                  )}

                  <HStack>
                    {isReplaceLogin && (
                      <Button
                        onClick={() =>
                          setUser((u: any) => ({
                            ...u,
                            tempPasswordForced: false,
                          }))
                        }
                        bgColor={theme.dark}
                        color='white'
                        size='lg'
                        w='full'
                        mt={2}
                        _hover={{ bg: 'blue.600' }}
                      >
                        Annuler
                      </Button>
                    )}
                    <Button
                      onClick={handleSubmit}
                      bgColor={theme.dark}
                      color='white'
                      size='lg'
                      w='full'
                      mt={2}
                      _hover={{ bg: 'blue.600' }}
                    >
                      {!isLogin ? 'Mettre à jour' : 'Se connecter'}
                    </Button>
                  </HStack>
                </VStack>
              )}

              {/* Formulaire */}
            </VStack>
          </Box>
        </Flex>
      </Container>

      {/* Footer */}
      <Box
        position='absolute'
        bottom={4}
        left='50%'
        transform='translateX(-50%)'
      >
        <Text fontSize='xs' color='gray.500' textAlign='center'>
          © 2024 EvisionCRM. Tous droits réservés.
        </Text>
      </Box>
    </Box>
    //     <Heading mb='15px'>
    //       <Flex alignItems='center' justifyContent='center'>
    //         <img
    //           src={
    //             isExternal
    //               ? `logo${localStorage.getItem('company')}big.png`
    //               : '/logoEVision.png'
    //           }
    //           style={{ height: '50px', marginRight: '7px' }}
    //         />
    //         {!isExternal && 'EVisionCRM'}
    //       </Flex>
    //     </Heading>
  );
};

export default Login;
